// ── Design decisions ──
// Orquestador del paso 2 de agencia. Vive fuera de EncomiendaWizard.js para que
// el asistente sólo gane dos costuras (llamar este hook y renderizar
// <AgencyStep2/> cuando el tenant es agencia) y el resto del asistente quede
// byte a byte como está para los demás tenants.
//
// · Una sola vía de escritura: los artículos se guardan con el saveItemOnBlur
//   del asistente (con su guardia de INSERT en vuelo); los paquetes se crean con
//   handleAddPackage y se borran con handleDeletePackage; las cajas se borran
//   con deleteCaja (sus guardas de Boxful / manifiesto cerrado / guía siguen
//   ahí). Lo nuevo (aplicar grupos, mover artículos, destinatario por paquete,
//   upsert de caja serializado) vive en el adaptador src/api/supabase/agencyEncomienda.
// · "Sucio" se lleva en un ref (tempId → pkgId): flush() guarda cada fila
//   pendiente y espera los INSERT/UPDATE en vuelo ANTES de rehidratar desde la
//   base, así una edición que todavía no viajó nunca la pisa un dato viejo.
// · Rehidratar después de Aplicar: todas las filas vuelven con id real (los
//   grupos se insertan con .select()). No se agregan filas vacías fantasma.
// · Cajas: autoguardado con debounce (600 ms) sólo cuando la caja es válida
//   (peso > 0, dimensiones, ≥1 paquete); cada caja tiene su cola serializada
//   (crearColaCajas) que conserva únicamente el último estado en espera, y la
//   tarea lee el estado vigente al ejecutarse — dos guardados solapados dejan
//   box_packages exactamente como la UI. Un paquete vive en una sola caja:
//   asignarlo a otra lo quita de la anterior y ambas se reguardan.
// · El destinatario de la caja sale del paquete principal (primero), no del
//   paso 1.
// · El umbral de impuesto nunca se escribe acá: customs_config se carga una
//   vez (cargarCustomsConfig) y AgencyStep2 deriva el tramo vía customsCalc.
// · Silencio: nada de toasts; los estados "guardando…/guardado" son por fila.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../../api/supabase/agencyEncomienda';

const DEBOUNCE_CAJA_MS = 600;
const SAVED_FLASH_MS = 2000;

export const nuevaCaja = (n) => ({
  localId: `caja-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  dbBoxId: null,
  caja_number: n,
  weight_lb: '',
  dimensions: '',
  packageIds: [],
  saved: false,
  saving: false,
  error: null,
});

export const cajaValida = (c) =>
  Boolean(c) &&
  parseFloat(c.weight_lb) > 0 &&
  Boolean(String(c.dimensions || '').trim()) &&
  Array.isArray(c.packageIds) &&
  c.packageIds.length > 0;

export const esFilaVacia = (it) =>
  !String(it?.description || '').trim() && !(parseFloat(it?.unitValue) > 0);

export default function useAgencyStep2Controller({
  enabled,
  tenantId,
  currentManifestId,
  encomiendaDbId,
  encomiendaPublicId,
  customerId,
  defaultRecipient,
  savedDestinatarios,
  packages,
  setPackages,
  pkgIds,
  setPkgIds,
  cajas,
  setCajas,
  emptyItem,
  saveItemOnBlur,
  removeItem,
  handleAddPackage,
  handleDeletePackage,
  deleteCaja,
  inFlightItemSaves,
  analyzingPackageId,
  setAnalyzingPackageId,
}) {
  const [saveStates, setSaveStates] = useState({});
  const [customsConfig, setCustomsConfig] = useState(null);
  const [notice, setNotice] = useState(null);

  const dirtyRef = useRef(new Map()); // tempId → pkgId
  const packagesRef = useRef(packages);
  packagesRef.current = packages;
  const cajasRef = useRef(cajas);
  cajasRef.current = cajas;
  const pkgIdsRef = useRef(pkgIds);
  pkgIdsRef.current = pkgIds;
  const colaRef = useRef(null);
  if (!colaRef.current) colaRef.current = api.crearColaCajas();
  const timersRef = useRef(new Map()); // localId → timeout
  const flashRef = useRef(new Map()); // tempId → timeout
  const manifestRef = useRef({ input: undefined, id: null });

  // ── Config de aduana (una vez) ──
  useEffect(() => {
    if (!enabled) return undefined;
    let vivo = true;
    api
      .cargarCustomsConfig()
      .then((cfg) => vivo && setCustomsConfig(cfg))
      .catch(() => vivo && setCustomsConfig(null));
    return () => {
      vivo = false;
    };
  }, [enabled]);

  useEffect(() => {
    const timers = timersRef.current;
    const flashes = flashRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      flashes.forEach((t) => clearTimeout(t));
    };
  }, []);

  const resolverManifest = useCallback(async () => {
    if (manifestRef.current.input === currentManifestId && manifestRef.current.id !== undefined) {
      return manifestRef.current.id;
    }
    const id = await api.resolverManifestId(currentManifestId || null);
    manifestRef.current = { input: currentManifestId, id };
    return id;
  }, [currentManifestId]);

  const marcarEstado = useCallback((tempId, estado) => {
    setSaveStates((prev) => {
      const next = { ...prev };
      if (estado) next[tempId] = estado;
      else delete next[tempId];
      return next;
    });
    const prevTimer = flashRef.current.get(tempId);
    if (prevTimer) clearTimeout(prevTimer);
    if (estado === 'saved') {
      const t = setTimeout(() => {
        flashRef.current.delete(tempId);
        setSaveStates((prev) => {
          if (prev[tempId] !== 'saved') return prev;
          const next = { ...prev };
          delete next[tempId];
          return next;
        });
      }, SAVED_FLASH_MS);
      flashRef.current.set(tempId, t);
    }
  }, []);

  // ── Artículos ──
  const buscarItem = (pkgId, tempId) =>
    (packagesRef.current[pkgId]?.items || []).find((it) => it.tempId === tempId) || null;

  const onUpdateItem = useCallback(
    (pkgId, tempId, patch) => {
      dirtyRef.current.set(tempId, pkgId);
      setPackages((p) => ({
        ...p,
        [pkgId]: {
          ...p[pkgId],
          items: (p[pkgId]?.items || []).map((it) => (it.tempId === tempId ? { ...it, ...patch } : it)),
        },
      }));
    },
    [setPackages]
  );

  const guardarItem = useCallback(
    async (pkgId, item) => {
      if (!item) return null;
      if (!item.id && esFilaVacia(item)) return null;
      marcarEstado(item.tempId, 'saving');
      try {
        const id = await saveItemOnBlur(pkgId, item);
        dirtyRef.current.delete(item.tempId);
        marcarEstado(item.tempId, 'saved');
        return id ?? item.id ?? null;
      } catch (err) {
        marcarEstado(item.tempId, null);
        throw err;
      }
    },
    [saveItemOnBlur, marcarEstado]
  );

  const onItemBlur = useCallback(
    (pkgId, item) => {
      const actual = buscarItem(pkgId, item?.tempId) || item;
      return guardarItem(pkgId, { ...actual, ...(item || {}) });
    },
    [guardarItem]
  );

  const onAddItem = useCallback(
    (pkgId, extra = {}) => {
      const it = { ...emptyItem(), ...extra };
      setPackages((p) => ({
        ...p,
        [pkgId]: { ...p[pkgId], items: [...(p[pkgId]?.items || []), it] },
      }));
      return it.tempId;
    },
    [emptyItem, setPackages]
  );

  const onRemoveItem = useCallback(
    async (pkgId, tempId) => {
      dirtyRef.current.delete(tempId);
      await removeItem(pkgId, tempId);
    },
    [removeItem]
  );

  // Flush: guarda las filas sucias y espera los INSERT/UPDATE en vuelo.
  const flush = useCallback(async () => {
    const pendientes = Array.from(dirtyRef.current.entries());
    for (const [tempId, pkgId] of pendientes) {
      const it = buscarItem(pkgId, tempId);
      if (!it) {
        dirtyRef.current.delete(tempId);
        continue;
      }
      try {
        await guardarItem(pkgId, it);
      } catch (_) {
        // se reporta por fila; no bloquea el flush de las demás
      }
    }
    const enVuelo = inFlightItemSaves?.current ? Array.from(inFlightItemSaves.current) : [];
    if (enVuelo.length > 0) await Promise.allSettled(enVuelo);
  }, [guardarItem, inFlightItemSaves]);

  const rehydrate = useCallback(async () => {
    if (!encomiendaDbId) return;
    const rows = await api.cargarPaquetes({ encomiendaId: encomiendaDbId, tenantId });
    const ids = rows.map((r) => r.row.id);
    setPkgIds(ids);
    setPackages((prev) => {
      const next = {};
      rows.forEach((r) => {
        next[r.row.id] = {
          ...(prev[r.row.id] || {}),
          receiptUrl: r.row.receipt_url || prev[r.row.id]?.receiptUrl || null,
          photoUrl: r.row.photo_url || prev[r.row.id]?.photoUrl || null,
          recipient: r.recipient,
          package_number: r.row.package_number,
          items: r.items,
        };
      });
      return next;
    });
    dirtyRef.current.clear();
  }, [encomiendaDbId, tenantId, setPkgIds, setPackages]);

  // ── Aplicar grupos (desde el drawer) ──
  const onAplicarGrupo = useCallback(
    async (grupo, { packageNumber } = {}) => {
      if (!encomiendaDbId) throw new Error('La encomienda todavía no está creada.');
      const manifestId = await resolverManifest();
      return api.aplicarGrupo({
        grupo,
        ctx: {
          encomiendaId: encomiendaDbId,
          tenantId,
          packageNumber: packageNumber ?? pkgIdsRef.current.length + 1,
          manifestId,
          defaultRecipient,
        },
      });
    },
    [encomiendaDbId, tenantId, defaultRecipient, resolverManifest]
  );

  const onAplicado = useCallback(async () => {
    await flush();
    await rehydrate();
  }, [flush, rehydrate]);

  // ── Paquetes ──
  const onAddPackage = useCallback(async () => {
    const id = await handleAddPackage();
    return id ?? null;
  }, [handleAddPackage]);

  const onMoveItem = useCallback(
    async (pkgId, tempId, target) => {
      const item = buscarItem(pkgId, tempId);
      if (!item) return;
      let targetId = target;
      if (target === 'nuevo') {
        targetId = await onAddPackage();
        if (!targetId) throw new Error('No se pudo crear el paquete nuevo.');
      }
      if (!targetId || targetId === pkgId) return;
      let id = item.id;
      if (!id && !esFilaVacia(item)) id = await guardarItem(pkgId, item);
      if (id) await api.moverItem({ itemId: id, targetPackageId: targetId });
      setPackages((p) => {
        const origen = (p[pkgId]?.items || []).filter((it) => it.tempId !== tempId);
        const movido = { ...item, id: id ?? item.id };
        return {
          ...p,
          [pkgId]: { ...p[pkgId], items: origen },
          [targetId]: { ...p[targetId], items: [...(p[targetId]?.items || []), movido] },
        };
      });
      if (dirtyRef.current.has(tempId)) dirtyRef.current.set(tempId, targetId);
    },
    [onAddPackage, guardarItem, setPackages]
  );

  const programarGuardadoCaja = useCallback(
    (localId) => {
      const prev = timersRef.current.get(localId);
      if (prev) clearTimeout(prev);
      const t = setTimeout(() => {
        timersRef.current.delete(localId);
        const cajaAhora = cajasRef.current.find((c) => c.localId === localId);
        if (!cajaValida(cajaAhora)) return;
        colaRef.current.encolar(localId, async () => {
          const caja = cajasRef.current.find((c) => c.localId === localId);
          if (!cajaValida(caja)) return;
          setCajas((cs) => cs.map((c) => (c.localId === localId ? { ...c, saving: true, error: null } : c)));
          try {
            const manifestId = await resolverManifest();
            const primaryId = caja.packageIds[0];
            const recipient = packagesRef.current[primaryId]?.recipient || defaultRecipient;
            const { boxId } = await api.upsertCaja({
              caja,
              ctx: { tenantId, customerId, encomiendaId: encomiendaDbId, manifestId },
              recipient,
            });
            setCajas((cs) =>
              cs.map((c) => {
                if (c.localId !== localId) return c;
                // Si cambió mientras viajaba, la cola ya tiene otra tarea: no
                // marcamos "saved" sobre un estado distinto al guardado.
                const igual =
                  c.weight_lb === caja.weight_lb &&
                  c.dimensions === caja.dimensions &&
                  c.packageIds.join('|') === caja.packageIds.join('|');
                return { ...c, dbBoxId: boxId, saving: false, saved: igual ? true : c.saved, error: null };
              })
            );
          } catch (err) {
            setCajas((cs) =>
              cs.map((c) =>
                c.localId === localId
                  ? { ...c, saving: false, saved: false, error: err?.message || 'No se pudo guardar la caja.' }
                  : c
              )
            );
          }
        });
      }, DEBOUNCE_CAJA_MS);
      timersRef.current.set(localId, t);
    },
    [setCajas, resolverManifest, tenantId, customerId, encomiendaDbId, defaultRecipient]
  );

  const onDeletePackage = useCallback(
    async (pkgId) => {
      await handleDeletePackage(pkgId);
      const afectadas = cajasRef.current.filter((c) => c.packageIds.includes(pkgId)).map((c) => c.localId);
      if (afectadas.length > 0) {
        setCajas((cs) =>
          cs.map((c) =>
            afectadas.includes(c.localId)
              ? { ...c, packageIds: c.packageIds.filter((id) => id !== pkgId), saved: false }
              : c
          )
        );
        afectadas.forEach((localId) => programarGuardadoCaja(localId));
      }
    },
    [handleDeletePackage, setCajas, programarGuardadoCaja]
  );

  const onUpdateRecipient = useCallback(
    async (pkgId, recipient) => {
      await api.actualizarDestinatario({ packageId: pkgId, recipient });
      setPackages((p) => ({ ...p, [pkgId]: { ...p[pkgId], recipient } }));
      // Las cajas cuyo paquete principal es éste llevan sus columnas de destinatario.
      cajasRef.current
        .filter((c) => c.packageIds[0] === pkgId && c.dbBoxId)
        .forEach((c) => {
          setCajas((cs) => cs.map((x) => (x.localId === c.localId ? { ...x, saved: false } : x)));
          programarGuardadoCaja(c.localId);
        });
    },
    [setPackages, setCajas, programarGuardadoCaja]
  );

  // ── Cajas ──
  const onAddCaja = useCallback(() => {
    setCajas((cs) => [...cs, nuevaCaja(cs.length + 1)]);
  }, [setCajas]);

  const onUpdateCaja = useCallback(
    (localId, patch) => {
      setCajas((cs) => cs.map((c) => (c.localId === localId ? { ...c, ...patch, saved: false, error: null } : c)));
      programarGuardadoCaja(localId);
    },
    [setCajas, programarGuardadoCaja]
  );

  const onAssignPackageToCaja = useCallback(
    (pkgId, localId) => {
      const afectadas = new Set();
      cajasRef.current.forEach((c) => {
        if (c.packageIds.includes(pkgId)) afectadas.add(c.localId);
      });
      if (localId) afectadas.add(localId);
      setCajas((cs) =>
        cs.map((c) => {
          const sin = c.packageIds.filter((id) => id !== pkgId);
          if (c.localId === localId) {
            return { ...c, packageIds: [...sin, pkgId], saved: false, error: null };
          }
          if (sin.length !== c.packageIds.length) return { ...c, packageIds: sin, saved: false, error: null };
          return c;
        })
      );
      afectadas.forEach((id) => programarGuardadoCaja(id));
    },
    [setCajas, programarGuardadoCaja]
  );

  const onDeleteCaja = useCallback(
    async (localId) => {
      const t = timersRef.current.get(localId);
      if (t) {
        clearTimeout(t);
        timersRef.current.delete(localId);
      }
      await deleteCaja(localId);
    },
    [deleteCaja]
  );

  const onAnalyzeReceipt = useCallback((pkgId) => setAnalyzingPackageId(pkgId), [setAnalyzingPackageId]);

  // ── Paquetes de sesión (forma que consume AgencyStep2) ──
  const paquetes = useMemo(
    () =>
      pkgIds.map((id, i) => {
        const p = packages[id] || {};
        return {
          id,
          package_number: p.package_number ?? i + 1,
          recipient: p.recipient || defaultRecipient,
          cajaLocalId: cajas.find((c) => c.packageIds.includes(id))?.localId ?? null,
          items: p.items || [],
          receiptUrl: p.receiptUrl || null,
          photoUrl: p.photoUrl || null,
        };
      }),
    [pkgIds, packages, cajas, defaultRecipient]
  );

  const props = {
    encomiendaPublicId,
    encomiendaDbId,
    tenantId,
    customerId,
    paquetes,
    cajas,
    destinatarios: savedDestinatarios || [],
    customsConfig,
    defaultRecipient,
    api,
    notice,
    onAddItem,
    onUpdateItem,
    onItemBlur,
    onRemoveItem,
    onMoveItem,
    saveStates,
    onAddPackage,
    onDeletePackage,
    onUpdateRecipient,
    onAddCaja,
    onUpdateCaja,
    onDeleteCaja,
    onAssignPackageToCaja,
    onAplicarGrupo,
    onAplicado,
    onAnalyzeReceipt,
    analyzingPackageId,
  };

  return { props, flush, rehydrate, setNotice };
}
